/**
 * Integrations page content. Mirrors the `compare.ts` pattern: one entry per
 * integration, static routes for the entries that earn a detail page (the
 * prerenderer skips param routes, so each detail page is a thin wrapper).
 *
 * CLAIMS DISCIPLINE — this file is the reason the page is safe to ship.
 * `status` is not marketing copy, it is a factual statement about code that
 * runs today, and it was set from a call-site survey (2026-07-30), not from
 * the package list. `packages/subscriber/src/mailchimp.ts` exists, ships with
 * a test suite, and has ZERO call sites — so Mailchimp is `planned`, not
 * `live`. Same for PostHog. MailerLite/Brevo run for exactly one client, so
 * they are not listed as platform capabilities at all.
 *
 * Rules for editing:
 *   - `live`     => it runs in production for platform clients TODAY. Grep the
 *                   call sites before you promote anything to this.
 *   - `launching` => built and merged, not yet deployed to any environment.
 *                   Must carry a `caveat` saying so.
 *   - `planned`  => on the roadmap, not built. Never implies a date.
 *   - `request`  => we would build it for a customer who needs it.
 *
 * Roadmap + reasoning: docs/pm/active/fugue-lab-integrations.md
 * Do not add pricing here; /packages is the single source (see pricing.ts).
 */

export type IntegrationStatus = 'live' | 'launching' | 'planned' | 'request'

/** Native = we run it · Connected = their account, we sync · Open = anything else. */
export type IntegrationTier = 'native' | 'connected' | 'open'

export type IntegrationCategory =
  | 'Email & marketing'
  | 'Payments'
  | 'Calendar & scheduling'
  | 'Accounting'
  | 'Donor & CRM'
  | 'Ticketing'
  | 'Analytics'
  | 'Accounts & sign-in'
  | 'Automation'

export interface Integration {
  slug: string
  name: string
  category: IntegrationCategory
  tier: IntegrationTier
  status: IntegrationStatus
  /** One line for the index card. Plain, factual, no adjectives. */
  summary: string
  /** Shown next to the status chip when the status needs qualifying. */
  caveat?: string
  /** Present only when the integration earns its own page. */
  detail?: IntegrationDetail
}

export interface IntegrationDetail {
  seoTitle: string
  seoDescription: string
  heroHeadline: string
  heroBody: string
  /** The honest paragraph: what this does and does not do today. */
  shortVersion: string
  /** What you get, concretely. */
  capabilities: string[]
  /** What it does NOT do. Non-negotiable — every detail page has one. */
  limitations: string[]
  setupSteps: { title: string; description: string }[]
  faqs: { question: string; answer: string }[]
}

export const STATUS_LABELS: Record<IntegrationStatus, string> = {
  live: 'Available now',
  launching: 'Built, not yet live',
  planned: 'On the roadmap',
  request: 'Available on request',
}

export const TIER_COPY: Record<IntegrationTier, { label: string; blurb: string }> = {
  native: {
    label: 'Native',
    blurb:
      'We run it. Nothing to connect, nothing to pay a second vendor for, and it feeds your patron record directly, so there is no sync to fall behind.',
  },
  connected: {
    label: 'Connected',
    blurb:
      'Your account, your data, your billing relationship. You connect it once and we keep the two sides in step. Keep the tool your team already knows.',
  },
  open: {
    label: 'Open',
    blurb:
      'Everything else. Send your data anywhere with outbound webhooks, Zapier or Make, or move it by CSV. No integration needed on our side, and no waiting on us.',
  },
}

export const integrations: Integration[] = [
  // ---------------------------------------------------------------- Native
  {
    slug: 'email-delivery',
    name: 'Email delivery',
    category: 'Email & marketing',
    tier: 'native',
    status: 'live',
    summary:
      'Audience lists, scheduled sends, welcome sequences and per-organization open and click tracking, built in. No separate email platform required.',
    detail: {
      seoTitle: 'Built-in Email for Music Organizations',
      seoDescription:
        'Fugue Lab sends your newsletters, welcome sequences and transactional email natively, from your own audience list, with open and click tracking per organization.',
      heroHeadline: 'Your mailing list, on the same record as your ticket buyers',
      heroBody:
        'Most music organizations keep an email list in one tool, ticket buyers in another, and donors in a third. Fugue Lab sends email from the same contact record the rest of your operation writes to.',
      shortVersion:
        'Email is native, not an integration. Your audience list, scheduled sends, welcome sequences and open/click tracking all run inside Fugue Lab, and every send draws on the same contact record your box office, donation form and application funnel write to. That is the whole argument for native: there is no sync to fall behind, and a ticket buyer who donates is one person, not three rows in three tools.',
      capabilities: [
        'Audience lists built from your real contact record, not a separate import',
        'Scheduled sends and welcome sequences',
        'Open and click tracking per organization',
        'Transactional email from the same system: receipts, tickets, application confirmations',
      ],
      limitations: [
        'This is not a replacement for a full marketing-automation suite. There is no visual journey builder, no A/B testing and no SMS today.',
        'If you already run Mailchimp or Constant Contact and want to keep it, that is a Connected-tier integration and is on the roadmap rather than available today.',
      ],
      setupSteps: [
        {
          title: 'We verify your sending domain',
          description:
            'Email goes out from your own domain so it lands in inboxes and looks like you. We handle the DNS records as part of onboarding.',
        },
        {
          title: 'Your existing list comes across',
          description:
            'Send us a CSV export from whatever you use today. Contacts merge into the patron record rather than sitting in a separate list.',
        },
        {
          title: 'You write and send',
          description: 'Compose in the app, pick an audience, send now or schedule it.',
        },
      ],
      faqs: [
        {
          question: 'Do I have to give up Mailchimp?',
          answer:
            'Not permanently. Today, native email is the supported path and you would move your list across. Keeping Mailchimp as your sending platform while Fugue Lab feeds it contacts is a Connected-tier integration on our roadmap, tell us if you need it and it moves up the queue.',
        },
        {
          question: 'Can I still send from my own address?',
          answer: 'Yes. We verify your domain during onboarding so mail comes from your organization, not from us.',
        },
      ],
    },
  },
  {
    slug: 'ticketing-and-donations',
    name: 'Ticketing & donations',
    category: 'Ticketing',
    tier: 'native',
    status: 'launching',
    caveat: 'Built and tested. Not yet switched on in a live environment, talk to us about timing.',
    summary:
      'Sell tickets and take donations on your own site, with the buyer landing on the same contact record as your mailing list.',
  },

  // ------------------------------------------------------------- Connected
  {
    slug: 'stripe',
    name: 'Stripe',
    category: 'Payments',
    tier: 'connected',
    status: 'launching',
    caveat: 'Built against Stripe Connect. Live payment testing is still in progress.',
    summary:
      'Take card payments for tickets, donations and application fees. Money goes to your Stripe account directly, we never hold your funds.',
    detail: {
      seoTitle: 'Stripe Payments for Music Festivals and Churches',
      seoDescription:
        'Connect your own Stripe account to Fugue Lab for ticket sales, donations and application fees. Direct charges to your account, Fugue Lab never holds your money.',
      heroHeadline: 'Your Stripe account, your money, your payout schedule',
      heroBody:
        'Fugue Lab uses Stripe Connect with direct charges, which means payments land in your Stripe account, not in a Fugue Lab balance that we pay out from later.',
      shortVersion:
        "You connect the Stripe account you already have, or create one during onboarding. Ticket sales, donations and application fees charge directly to it. That matters for two reasons: your money is never sitting in someone else's balance waiting on a payout schedule, and your books stay in Stripe where your treasurer can already see them.",
      capabilities: [
        'Direct charges to your own Stripe account, funds never pass through Fugue Lab',
        'Ticket sales, donations and application fees on one connection',
        'Refunds and disputes handled in the Stripe dashboard you already use',
        'Your existing payout schedule and bank account carry over unchanged',
      ],
      limitations: [
        'Built and merged, but not yet switched on in a live environment. Live payment testing is gated on a Stripe test Connect account, so treat availability as a conversation rather than a date.',
        "Stripe fees are Stripe's and are billed to you directly. See /packages for what Fugue Lab charges.",
      ],
      setupSteps: [
        {
          title: 'Connect your Stripe account',
          description:
            'A standard Stripe Connect flow. If you do not have an account, you can create one in the same step.',
        },
        {
          title: 'Choose what it handles',
          description: 'Tickets, donations, application fees, or all three.',
        },
        {
          title: 'Money goes straight to you',
          description: 'Charges are direct, so your payouts run on the schedule you already have with Stripe.',
        },
      ],
      faqs: [
        {
          question: 'Does Fugue Lab hold my money?',
          answer:
            'No. Charges go directly to your Stripe account. We are not in the payment path as a holder of funds.',
        },
        {
          question: 'What if we already use Stripe for something else?',
          answer:
            'That is the normal case, and it is fine. Connecting to Fugue Lab does not disturb existing products, subscriptions or payment links in your account.',
        },
      ],
    },
  },
  {
    slug: 'paypal',
    name: 'PayPal',
    category: 'Payments',
    tier: 'connected',
    status: 'launching',
    caveat: 'Built. Not yet switched on in a live environment.',
    summary:
      'Take PayPal payments alongside cards, for audiences and donors who prefer it. Connects to your own PayPal account.',
  },
  {
    slug: 'google-calendar',
    name: 'Google Calendar',
    category: 'Calendar & scheduling',
    tier: 'connected',
    status: 'live',
    summary:
      'Sync services, rehearsals and events to Google Calendar so your musicians see the schedule where they already look.',
    detail: {
      seoTitle: 'Google Calendar Sync for Choirs and Music Organizations',
      seoDescription:
        'Sync rehearsals, services and events from Fugue Lab to Google Calendar so your choir and staff see the schedule in the calendar they already use.',
      heroHeadline: 'Your schedule where people actually look for it',
      heroBody:
        "A rehearsal schedule nobody opens is not a schedule. Fugue Lab pushes services, rehearsals and events into Google Calendar so they land next to everything else in someone's week.",
      shortVersion:
        "Fugue Lab holds the authoritative schedule (services, rehearsals, call times, and the liturgical layer if you use Canticle) and syncs it out to Google Calendar. Your singers and staff do not need a Fugue Lab login to know when to show up. The sync is one-way by design: the schedule has one owner, so an accidental drag in someone's calendar cannot silently move a service.",
      capabilities: [
        'Services, rehearsals and events sync automatically as they change',
        'People see the schedule in the calendar app they already use, on any device',
        "Works alongside Canticle's liturgical planning for parishes",
      ],
      limitations: [
        'The sync is one-way, deliberately: Fugue Lab is the source of truth and edits made in Google Calendar do not flow back.',
        'Apple Calendar and Outlook are supported by iCal subscription rather than a native two-way integration.',
      ],
      setupSteps: [
        {
          title: 'Tell us which calendar',
          description:
            "We point the sync at a specific calendar (usually a shared one your organization already has) rather than someone's personal calendar.",
        },
        {
          title: 'We connect it',
          description: 'Handled during onboarding today. A self-serve Connect button is on the roadmap.',
        },
        {
          title: 'It stays in step',
          description: 'Change a rehearsal in Fugue Lab and the calendar follows.',
        },
      ],
      faqs: [
        {
          question: 'Can people edit the schedule from their calendar?',
          answer:
            'No, and that is on purpose. One system owns the schedule. If edits flowed both ways, a stray drag on a phone could move a service and nobody would know which version was right.',
        },
        {
          question: 'What about Apple Calendar or Outlook?',
          answer:
            'Both can subscribe to an iCal feed, which covers viewing the schedule. A native integration for those is on the roadmap.',
        },
      ],
    },
  },

  // ------------------------------------------------------------------ Open
  {
    slug: 'webhooks',
    name: 'Webhooks',
    category: 'Automation',
    tier: 'open',
    status: 'planned',
    summary:
      'Send events to any URL you control, in real time: a ticket sold, a donation received, an application submitted.',
  },
  {
    slug: 'zapier',
    name: 'Zapier & Make',
    category: 'Automation',
    tier: 'open',
    status: 'planned',
    summary:
      'One connection to thousands of downstream apps, mapped in the Zapier or Make interface without anyone writing code.',
  },
  {
    slug: 'csv',
    name: 'CSV import & export',
    category: 'Automation',
    tier: 'open',
    status: 'planned',
    summary:
      'Get your contacts, orders and applications out as a spreadsheet whenever you want, and bring existing lists in.',
  },

  // ---------------------------------------------------- Planned / on request
  {
    slug: 'planning-center',
    name: 'Planning Center',
    category: 'Donor & CRM',
    tier: 'connected',
    status: 'planned',
    summary: 'Sync your parish roster from Planning Center People so your choir list is not maintained twice.',
  },
  {
    slug: 'quickbooks',
    name: 'QuickBooks & Xero',
    category: 'Accounting',
    tier: 'connected',
    status: 'planned',
    summary: 'Push ticket and donation revenue into the books your treasurer already keeps.',
  },
  {
    slug: 'constant-contact',
    name: 'Constant Contact',
    category: 'Email & marketing',
    tier: 'connected',
    status: 'planned',
    summary: 'Keep sending from Constant Contact while Fugue Lab keeps the audience list current.',
  },
  {
    slug: 'mailchimp',
    name: 'Mailchimp',
    category: 'Email & marketing',
    tier: 'connected',
    status: 'planned',
    summary: 'Keep Mailchimp as your sending platform and let Fugue Lab keep the audience in sync.',
  },
  {
    slug: 'google-sign-in',
    name: 'Sign in with Google',
    category: 'Accounts & sign-in',
    tier: 'connected',
    status: 'planned',
    summary: 'Let applicants, jurors and staff sign in with the Google account they already have.',
  },
  {
    slug: 'donor-crm',
    name: 'Little Green Light & Bloomerang',
    category: 'Donor & CRM',
    tier: 'connected',
    status: 'request',
    summary: 'Keep your donor database in step with the people who buy tickets and attend events.',
  },
  {
    slug: 'eventbrite',
    name: 'Eventbrite import',
    category: 'Ticketing',
    tier: 'connected',
    status: 'request',
    summary: 'Bring your past Eventbrite attendees across so your audience history moves with you.',
  },
  {
    slug: 'square',
    name: 'Square',
    category: 'Payments',
    tier: 'connected',
    status: 'request',
    summary: 'Reconcile at-the-door card sales with the tickets and donations sold online.',
  },
  {
    slug: 'google-analytics',
    name: 'Google Analytics',
    category: 'Analytics',
    tier: 'connected',
    status: 'planned',
    summary: 'Put your own GA4 property on your Fugue Lab site so traffic reporting stays in one place.',
  },
]

/** Detail pages exist only for integrations with a real setup story. */
export const integrationsWithPages = integrations.filter((i) => i.detail)

export function getIntegration(slug: string): Integration {
  const found = integrations.find((i) => i.slug === slug)
  if (!found) throw new Error(`Unknown integration: ${slug}`)
  return found
}

export const CATEGORY_ORDER: IntegrationCategory[] = [
  'Email & marketing',
  'Payments',
  'Ticketing',
  'Calendar & scheduling',
  'Donor & CRM',
  'Accounting',
  'Accounts & sign-in',
  'Analytics',
  'Automation',
]
